/**
 * @file decision-form/cert-validity.ts
 * @description 證書有效期純函式。依驗證類型計算 validFrom/validUntil（YYYY-MM-DD）。
 *
 * 規則（見 spec §4.4）：
 * - 初次：validFrom = 初次驗證日期；validUntil = validFrom + 3年 - 1天
 * - 追蹤查驗一/二：沿用上年度（初次）證書有效期間，不變
 * - 展延：validFrom = 前一證書到期次日；validUntil = +3年 - 1天
 * 不使用 Date.now()；純計算，便於測試與 SSR。
 *
 * ⚠️ 非初次「證書資訊自動推算」（追查按年段、展延順延，客戶 PPT S3/S30）
 * 已由 cert-derive.ts 的 deriveCertInfo 承接（API cert-info-draft 走它）；
 * 本函式保留給前端初次流程「填初次驗證日期 → 連動算 3 年效期」使用。
 */
/**
 * 依驗證類型字串（clients.verificationType 字典值）粗分三類。
 * 無法判斷時回落 'initial'。
 */
export function classifyVerificationType(verificationType) {
    const t = verificationType ?? '';
    if (/展延|續證|延續|換證/.test(t))
        return 'renewal';
    if (/追蹤|後續|監督|定期/.test(t))
        return 'surveillance';
    if (/初次|首次|新申請/.test(t))
        return 'initial';
    return 'initial';
}
/** 把 'YYYY-MM-DD' 拆為 [y,m,d] 數字 */
function parse(dateStr) {
    const parts = dateStr.split('-').map((n) => parseInt(n, 10));
    return [parts[0], parts[1], parts[2]];
}
/** 格式化為 'YYYY-MM-DD'（補零） */
function fmt(y, m, d) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${y}-${pad(m)}-${pad(d)}`;
}
/** 加 n 天（用 UTC Date 純計算，避免時區）。供 cert-derive 等同層模組復用。 */
export function addDays(dateStr, days) {
    const [y, m, d] = parse(dateStr);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + days);
    return fmt(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}
/**
 * 加 n 年（同月同日）。閏日邊界：2/29 在目標年不存在時夾取為該月最後一天
 * （例 2024-02-29 + 1 年 = 2025-02-28），避免 JS Date 自動進位到 3/1。
 * 供 cert-derive 的年段推算復用。
 */
export function addYearsClamped(dateStr, years) {
    const [y, m, d] = parse(dateStr);
    const targetYear = y + years;
    // Date.UTC(targetYear, m, 0) = 目標年 m 月的最後一天（第 0 天 = 上月末）
    const lastDay = new Date(Date.UTC(targetYear, m, 0)).getUTCDate();
    return fmt(targetYear, m, Math.min(d, lastDay));
}
/** validFrom → validUntil = validFrom + 3年 - 1天 */
function threeYearsMinusOneDay(validFrom) {
    const [y, m, d] = parse(validFrom);
    // 先 +3 年同月日，再 -1 天
    const plus3 = fmt(y + 3, m, d);
    return addDays(plus3, -1);
}
export function computeCertValidity(input) {
    const { kind, firstAuditDate, priorValidFrom, priorValidUntil } = input;
    if (kind === 'surveillance') {
        return {
            validFrom: priorValidFrom ?? null,
            validUntil: priorValidUntil ?? null,
        };
    }
    if (kind === 'renewal') {
        if (!priorValidUntil)
            return { validFrom: null, validUntil: null };
        const validFrom = addDays(priorValidUntil, 1);
        return { validFrom, validUntil: threeYearsMinusOneDay(validFrom) };
    }
    // initial
    if (!firstAuditDate)
        return { validFrom: null, validUntil: null };
    return { validFrom: firstAuditDate, validUntil: threeYearsMinusOneDay(firstAuditDate) };
}
